import React from 'react';

/**
 * CAM360 Badge Icon
 * A unified single icon combining Contract LifeCycle Management
 * Document with building integrated - single cohesive symbol
 * Enhanced for better visibility and prominence
 *
 * @param {number} size - Size of the badge (width and height in pixels). Default: 36
 * @param {string} primaryColor - Primary color. Default: '#2D7DD2'
 * @param {string} secondaryColor - Secondary color. Default: '#1B66B1'
 */
const CAM360Badge = ({
  size = 36,
  primaryColor = '#2D7DD2',
  secondaryColor = '#1B66B1',
}) => {
  const viewBoxSize = 100;
  const center = viewBoxSize / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background Circle with gradient for depth */}
      <defs>
        <linearGradient id="badgeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={primaryColor} stopOpacity="1" />
          <stop offset="100%" stopColor={secondaryColor} stopOpacity="1" />
        </linearGradient>
        <filter id="shadow">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
        </filter>
      </defs>

      <circle
        cx={center}
        cy={center}
        r={center - 2}
        fill="url(#badgeGradient)"
        filter="url(#shadow)"
      />

      {/* Outer ring for prominence */}
      <circle
        cx={center}
        cy={center}
        r={center - 2}
        fill="none"
        stroke="white"
        strokeWidth="1"
        opacity="0.3"
      />

      {/* Inner decorative ring - more visible */}
      <circle
        cx={center}
        cy={center}
        r={center - 8}
        fill="none"
        stroke="white"
        strokeWidth="2"
        opacity="0.4"
      />

      {/* Unified Icon: Document with Building integrated */}
      <g transform="translate(30, 25)">
        {/* Main Document/Contract Base */}
        <rect
          x="0"
          y="0"
          width="40"
          height="50"
          rx="3"
          fill="white"
          opacity="1"
        />
        <rect
          x="0"
          y="0"
          width="40"
          height="50"
          rx="3"
          stroke="white"
          strokeWidth="2"
          fill="none"
        />

        {/* Document lines (contract text) */}
        <line
          x1="6"
          y1="8"
          x2="34"
          y2="8"
          stroke={primaryColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.9"
        />
        <line
          x1="6"
          y1="14"
          x2="28"
          y2="14"
          stroke={primaryColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.9"
        />
        <line
          x1="6"
          y1="20"
          x2="34"
          y2="20"
          stroke={primaryColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.9"
        />

        {/* Building/Asset integrated in the document (bottom section) */}
        <g transform="translate(8, 28)">
          {/* Building base integrated into document */}
          <rect
            x="0"
            y="0"
            width="24"
            height="18"
            rx="2"
            fill={primaryColor}
            opacity="0.15"
          />
          <rect
            x="0"
            y="0"
            width="24"
            height="18"
            rx="2"
            stroke={primaryColor}
            strokeWidth="2"
            fill="none"
            opacity="0.8"
          />

          {/* Building roof */}
          <path
            d="M -1 0 L 12 -6 L 25 0"
            stroke={primaryColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={primaryColor}
            opacity="0.3"
          />

          {/* Building windows */}
          <rect
            x="3"
            y="4"
            width="3.5"
            height="3.5"
            rx="0.5"
            fill={primaryColor}
            opacity="0.9"
          />
          <rect
            x="8.5"
            y="4"
            width="3.5"
            height="3.5"
            rx="0.5"
            fill={primaryColor}
            opacity="0.9"
          />
          <rect
            x="17.5"
            y="4"
            width="3.5"
            height="3.5"
            rx="0.5"
            fill={primaryColor}
            opacity="0.9"
          />

          <rect
            x="3"
            y="9"
            width="3.5"
            height="3.5"
            rx="0.5"
            fill={primaryColor}
            opacity="0.9"
          />
          <rect
            x="8.5"
            y="9"
            width="3.5"
            height="3.5"
            rx="0.5"
            fill={primaryColor}
            opacity="0.9"
          />
          <rect
            x="17.5"
            y="9"
            width="3.5"
            height="3.5"
            rx="0.5"
            fill={primaryColor}
            opacity="0.9"
          />

          {/* Building door */}
          <rect
            x="10.5"
            y="13"
            width="4"
            height="5"
            rx="0.5"
            fill={primaryColor}
            opacity="1"
          />
        </g>

        {/* Seal/Signature badge at top right of document */}
        <circle cx="32" cy="12" r="5" fill={primaryColor} opacity="0.6" />
        <path
          d="M 29.5 12 L 31 13.5 L 34.5 9.5"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
};

export default CAM360Badge;
